import React, { useRef } from "react"
import { useLottie } from "../../hooks/useLottie"
import illustration from "../../lottie/11-research.json"
import { connector, ContainerProps } from "./containers/Category.container"
import { CategoryEntity } from "../../entities/CategoryEntity"
import { Transition } from "@headlessui/react"
import { onSave } from "../../redux/editor/types"

const CategoryItem = ({ children, onClick, selected }) => (
  <div
    className="relative p-4 pt-4 text-center cursor-pointer group"
    onClick={() => onClick()}
  >
    <div
      className={`rounded-xl top-0 left-0 right-0 bottom-0 transition duration-300 ease-in-out transform absolute ${
        selected
          ? "bg-white z-10 scale-y-125 shadow-2xl"
          : "bg-gray-100 scale-100 shadow-none"
      } group-hover:bg-white group-hover:z-10 group-hover:scale-y-125 group-hover:shadow-2xl`}
    ></div>
    <span
      className={`${
        selected && "z-10 text-green-600"
      } group-hover:z-10 group-hover:text-green-600 relative text-gray-700 font-semibold transition-colors duration-300`}
    >
      {children}
    </span>
  </div>
)

const Illustration = () => {
  const ref = useRef()

  useLottie(ref, illustration)

  return <div ref={ref}></div>
}

export type CategoryProps = {
  isFetching: boolean
  onClose: Function
  onPublish: Function
  onSelect: Function
  categories: CategoryEntity[]
  selectedId: string | null
  isOpen: boolean
}

export const CategoryWrapper: React.FC<CategoryProps> = ({
  isOpen,
  onClose,
  onSelect,
  onPublish,
  categories,
  selectedId,
}) => {
  return (
    <Transition
      className="fixed top-0 bottom-0 left-0 right-0 z-10 duration-300 ease-in-out transform translate-y-0 bg-white"
      show={isOpen}
      enterFrom="translate-y-10 opacity-0"
      enterTo="translate-y-0 opacity-100"
      leaveFrom="translate-y-0 opacity-100"
      leaveTo="translate-y-10 opacity-0"
    >
      <>
        <div className="container h-screen px-4 mx-auto overflow-scroll">
          <div className="relative w-full p-8 mt-4 overflow-hidden rounded-3xl bg-gray-50 md:p-20 md:mt-10">
            <div className="block text-4xl font-extrabold tracking-tight text-gray-800 sm:text-5xl xl:text-6xl">
              Quelle est la thématique
              <br />
              <span className="font-normal">
                de votre <span className="text-green-600">haïku</span> ?
              </span>
            </div>
            <button
              type="button"
              onClick={() => onClose()}
              className="absolute top-0 right-0 z-10 inline-flex items-center justify-center p-4 text-gray-500 transition duration-300 ease-in-out cursor-pointer rounded-3xl hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500"
            >
              <svg
                className="w-8 h-8"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="absolute bottom-0 right-0 hidden pr-8 transform lg:block w-96 translate-y-1/3">
              <Illustration />
            </div>
          </div>

          <div className="grid w-full grid-cols-3 gap-4 pb-24 mt-8 sm:grid-cols-4 md:grid-cols-6">
            {categories.map(({ label, id }) => (
              <CategoryItem
                key={id}
                onClick={() => onSelect(id)}
                selected={id === selectedId}
              >
                {label}
              </CategoryItem>
            ))}
          </div>
        </div>

        <div className="w-full opacity-0">
          <div className="container p-4 py-4 mx-auto">
            <div className="w-full p-4 font-medium uppercase">
              Enregistrer et publier
            </div>
          </div>
        </div>

        <Transition
          className="fixed bottom-0 left-0 right-0 z-20 w-full transition duration-500 ease-in-out"
          show={!!selectedId}
          enter="transform"
          enterFrom="translate-y-1/4 scale-90 opacity-0"
          enterTo="translate-y-0 scale-100 opacity-100"
          leave="transform"
          leaveFrom="translate-y-0 scale-100 opacity-100"
          leaveTo="translate-y-1/4 scale-90 opacity-0"
        >
          <div className="container p-4 py-4 mx-auto tr">
            <button
              onClick={() => onPublish()}
              className="w-full p-4 font-medium text-gray-100 uppercase transition duration-300 bg-green-500 shadow-lg rounded-xl hover:shadow-none hover:bg-green-600"
            >
              Enregistrer et publier
            </button>
          </div>
        </Transition>
      </>
    </Transition>
  )
}

export const CategoryContainer: React.FC<ContainerProps> = props => (
  <CategoryWrapper {...props} />
)

export const Category = connector(CategoryContainer)
